import React, { useState } from 'react';

import { Alert, Button, Center, Grid, Modal, Skeleton, Stack, Title, useMantineTheme } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconExclamationCircle, IconPlus } from '@tabler/icons-react';
import InfoCard from 'components/InfoCard';
import InfoCardSection from 'components/InfoCard/InfoCardSection';
import AddPaymentMethodDrawer from 'components/payment-methods/AddPaymentMethodDrawer';
import BacsDebitPaymentMethodCard from 'components/payment-methods/BacsDebitPaymentMethodCard';
import BankAccountPaymentMethodCard from 'components/payment-methods/BankAccountPaymentMethodCard';
import CreditCardPaymentMethodCard from 'components/payment-methods/CreditCardPaymentMethodCard';
import DeletePaymentMethodModal from 'components/payment-methods/DeletePaymentMethodModal';
import EditPaymentMethodForm from 'components/payment-methods/EditPaymentMethodForm';
import UnlockPaymentMethodModal from 'components/payment-methods/UnlockPaymentMethodModal';
import SharedPaymentMethodsAlert from 'components/SharedPaymentMethodsAlert';
import dayjs from 'dayjs';
import useAthleteIsSharingPaymentMethods from 'hooks/athlete/useAthleteIsSharingPaymentMethods';
import { useAthleteTracking } from 'hooks/mixpanel/useAthleteTracking';
import usePaymentMethods from 'hooks/paymentMethod/usePaymentMethods';
import { AthleteRetrieve } from 'types';
import { getPaymentMethodName } from 'utils/formatters';

interface IPaymentMethodsContentProps {
  athlete?: AthleteRetrieve;
  permission?: boolean;
}

const PaymentMethodsContent = ({ athlete, permission = true }: IPaymentMethodsContentProps): JSX.Element => {
  const theme = useMantineTheme();

  const [paymentMethodId, setPaymentMethodId] = useState<string>('');
  const [isPaymentMethodFormOpened, { open: openPaymentMethodForm, close: closePaymentMethodForm }] =
    useDisclosure(false);
  const [isEditFormOpen, { open: openEditForm, close: closeEditForm }] = useDisclosure(false);
  const [isDeleteModalOpen, { open: openDeleteModal, close: closeDeleteModal }] = useDisclosure(false);
  const [isUnlockModalOpen, { open: openUnlockModal, close: closeUnlockModal }] = useDisclosure(false);
  const isSharingPaymentMethods = useAthleteIsSharingPaymentMethods(athlete?.athleteId);
  const { trackPaymentMethodAddNewClicked } = useAthleteTracking();

  const {
    data: paymentMethodData,
    isSuccess: paymentMethodIsSuccess,
    isError: paymentMethodIsError,
    isLoading: paymentMethodIsLoading,
  } = usePaymentMethods({
    athleteId: athlete?.athleteId || '',
  });

  const handleAddPaymentMethodClick = (): void => {
    trackPaymentMethodAddNewClicked();
    openPaymentMethodForm();
  };

  const handleOpenEditPaymentMethodForm = (id: string): void => {
    setPaymentMethodId(id);
    openEditForm();
  };

  const handleCloseEditPaymentMethodForm = (): void => {
    setPaymentMethodId('');
    closeEditForm();
  };

  const handleOpenPaymentMethodDeleteModal = (id: string): void => {
    setPaymentMethodId(id);
    openDeleteModal();
  };

  const handleOpenPaymentMethodUnlockModal = (id: string): void => {
    setPaymentMethodId(id);
    openUnlockModal();
  };

  const paymentMethodsContent = (
    <Stack spacing='xs'>
      <Grid align='top'>
        {paymentMethodIsLoading && <Skeleton />}

        {paymentMethodIsError && (
          <Center sx={{ flexGrow: 1 }} p='lg'>
            <Alert icon={<IconExclamationCircle size={theme.fontSizes.md} />} title='Error' color='red'>
              Something went wrong while fetching data!
            </Alert>
          </Center>
        )}

        {paymentMethodIsSuccess &&
          paymentMethodData &&
          paymentMethodData.pages.map(page => {
            return page.data.results.length ? (
              page.data.results.map(result => {
                if (result.type === 'CARD') {
                  const expMonth = result.metadata?.expMonth;
                  const expYear = result.metadata?.expYear;
                  const expirationDate =
                    expMonth && expYear ? dayjs(`${expYear}/${expMonth}`).format('MM/YY') : '';
                  return (
                    <Grid.Col xs={6} sm={6} md={4} lg={6} xl={4} key={result.paymentMethodId}>
                      <CreditCardPaymentMethodCard
                        country={result.metadata?.billingDetails?.address?.country}
                        name={getPaymentMethodName(result)}
                        isDefault={result.isDefault}
                        zip={result.metadata?.billingDetails?.address?.postalCode || ''}
                        athlete={athlete}
                        expirationDate={expirationDate}
                        paymentMethodId={result.paymentMethodId}
                        openEditPaymentMethodForm={handleOpenEditPaymentMethodForm}
                        openPaymentMethodDeleteModal={handleOpenPaymentMethodDeleteModal}
                        permission={permission}
                        isLocked={result.isLocked}
                        failureMessage={result.failureMessage}
                        handleUnlock={handleOpenPaymentMethodUnlockModal}
                      />
                    </Grid.Col>
                  );
                }
                if (result.type === 'US BANK ACCOUNT' || result.type === 'ACSS DEBIT') {
                  return (
                    <Grid.Col xs={6} sm={6} md={4} lg={6} xl={4} key={result.paymentMethodId}>
                      <BankAccountPaymentMethodCard
                        type={result.type}
                        name={getPaymentMethodName(result)}
                        isDefault={result.isDefault}
                        routingNumber={result.metadata?.routingNumber || ''}
                        last4={result.metadata?.last4 || ''}
                        paymentMethodId={result.paymentMethodId}
                        athlete={athlete}
                        openEditPaymentMethodForm={handleOpenEditPaymentMethodForm}
                        openPaymentMethodDeleteModal={handleOpenPaymentMethodDeleteModal}
                        permission={permission}
                        isLocked={result.isLocked}
                        failureMessage={result.failureMessage}
                        handleUnlock={handleOpenPaymentMethodUnlockModal}
                      />
                    </Grid.Col>
                  );
                }

                if (result.type === 'BACS DEBIT') {
                  return (
                    <Grid.Col xs={6} sm={6} md={4} lg={6} xl={4} key={result.paymentMethodId}>
                      <BacsDebitPaymentMethodCard
                        name={result.metadata?.billingDetails?.name}
                        last4={result.metadata?.last4}
                        sortCode={result.metadata?.sortCode}
                        isDefault={result.isDefault}
                        paymentMethodId={result.paymentMethodId}
                        isLocked={result.isLocked}
                        failureMessage={result.failureMessage}
                        athlete={athlete}
                        openEditPaymentMethodForm={handleOpenEditPaymentMethodForm}
                        openPaymentMethodDeleteModal={handleOpenPaymentMethodDeleteModal}
                        handleUnlock={handleOpenPaymentMethodUnlockModal}
                        permission={permission}
                      />
                    </Grid.Col>
                  );
                }

                return null;
              })
            ) : (
              <Center sx={{ flexGrow: 1 }} p='lg'>
                <Stack spacing='sm'>
                  <Title order={4}>No results found</Title>
                </Stack>
              </Center>
            );
          })}
      </Grid>
      {isSharingPaymentMethods && <SharedPaymentMethodsAlert sx={{ marginBottom: '1rem' }} />}
    </Stack>
  );

  return (
    <>
      <InfoCard
        header={<Title order={4}>Payment Methods</Title>}
        actionButton={
          <Button
            onClick={handleAddPaymentMethodClick}
            compact
            loading={paymentMethodIsLoading}
            disabled={!permission || !athlete}
            leftIcon={<IconPlus size={theme.fontSizes.md} />}
          >
            Payment Method
          </Button>
        }
        content={<InfoCardSection content={paymentMethodsContent} />}
      />
      {athlete && (
        <AddPaymentMethodDrawer
          athleteId={athlete.athleteId}
          gymId={athlete.gym}
          isStaffContext
          opened={isPaymentMethodFormOpened}
          closeHandler={closePaymentMethodForm}
        />
      )}
      <EditPaymentMethodForm
        paymentMethodId={paymentMethodId}
        opened={isEditFormOpen}
        closeHandler={handleCloseEditPaymentMethodForm}
      />
      <Modal
        opened={isDeleteModalOpen}
        onClose={closeDeleteModal}
        title={<Title order={4}>Delete Payment Method</Title>}
        trapFocus
        centered
        size='auto'
      >
        <DeletePaymentMethodModal
          paymentMethodId={paymentMethodId}
          handleClose={closeDeleteModal}
          isPrimaryAcc={!athlete?.primaryAccount && !!athlete?.connectedType}
          isOwnerView
        />
      </Modal>
      <UnlockPaymentMethodModal
        paymentMethodId={paymentMethodId}
        opened={isUnlockModalOpen}
        close={closeUnlockModal}
        userRole='staff'
      />
    </>
  );
};

export default PaymentMethodsContent;
